import React, { useMemo, useRef, useState, useEffect } from "react";
import { CalendarIcon as CalendarIcon } from "@phosphor-icons/react";

/**
 * Typeable + picker date-of-birth input.
 *
 * Bug fix 2026-05-27 (Lauren reported): the original DOB field on
 * `/register` used `<input type="date">`. On iOS Safari that forces
 * the wheel picker, which makes scrolling back 30+ years feel
 * endless — the user who reported it actually abandoned. This
 * component:
 *
 *   1. PRIMARY input is `<input type="text" inputmode="numeric"
 *      autocomplete="bday">`. iOS shows the number pad, iOS
 *      Contacts auto-fills the birthday in one tap, and the user
 *      can just TYPE the date.
 *   2. SECONDARY tap target opens the native date picker (a hidden
 *      `<input type="date">` we trigger via `showPicker()`) for
 *      users who still prefer to pick visually.
 *   3. Smart formatter inserts the slashes as the user types so the
 *      flow is "0 5 2 7 1 9 9 0" without ever touching `/`.
 *   4. Accepts paste of common shapes: `MM/DD/YYYY`, `M/D/YYYY`,
 *      `YYYY-MM-DD`. Internally normalises to ISO `YYYY-MM-DD`
 *      before bubbling up via `onChange`, so callers don't have to
 *      know what the user typed.
 *
 * Props:
 *   value         — ISO string `YYYY-MM-DD` or "" (controlled).
 *   onChange      — `(isoOrEmpty: string) => void`. Called with "" when
 *                   the user clears the field or types something invalid.
 *   min / max     — ISO bounds passed through to the calendar picker
 *                   AND used for inline validation of typed input.
 *   id            — input id (for the <Label htmlFor> pairing).
 *   required      — passed to the visible text input.
 *   testId        — data-testid for the visible text input.
 *   className     — extra classes on the outer wrapper.
 */
export default function DateOfBirthInput({
  value = "",
  onChange,
  min,
  max,
  id = "dob",
  required = false,
  testId,
  className = "",
  // Pass "off" for non-birthday dates (whelp/appointment/expiry/etc) so
  // iOS doesn't suggest the user's own birthday from Contacts.
  autoComplete = "bday",
  placeholder = "MM/DD/YYYY",
  ariaLabel,
}) {
  const [text, setText] = useState(() => isoToDisplay(value));
  const calendarRef = useRef(null);

  // Sync local text when controlled value changes from outside (e.g. parent reset).
  useEffect(() => {
    setText(isoToDisplay(value));
  }, [value]);

  const onTextChange = (raw) => {
    const formatted = formatAsTyped(raw);
    setText(formatted);
    // Bubble up either a valid ISO date or "" — never a half-typed value.
    const iso = displayToIso(formatted);
    if (iso && withinBounds(iso, min, max)) {
      onChange?.(iso);
    } else if (formatted === "") {
      onChange?.("");
    } else {
      // Mid-typing — don't push partial values to the parent's gate.
      onChange?.("");
    }
  };

  const onTextPaste = (e) => {
    const pasted = (e.clipboardData?.getData("text") || "").trim();
    if (!pasted) return;
    const iso = displayToIso(pasted) || tryIsoPaste(pasted);
    if (iso) {
      e.preventDefault();
      setText(isoToDisplay(iso));
      if (withinBounds(iso, min, max)) onChange?.(iso);
    }
  };

  const openCalendar = () => {
    const el = calendarRef.current;
    if (!el) return;
    try {
      // Standardised on iOS 17+, Chrome 99+, Safari 16+, Firefox 101+.
      if (typeof el.showPicker === "function") {
        el.showPicker();
        return;
      }
    } catch (_) { /* fall through */ }
    // Fallback: just focus/click — older Safari opens the wheel on focus.
    el.focus();
    el.click();
  };

  const onCalendarChange = (e) => {
    const iso = e.target.value;
    setText(isoToDisplay(iso));
    if (iso && withinBounds(iso, min, max)) {
      onChange?.(iso);
    } else if (!iso) {
      onChange?.("");
    }
  };

  const hasInvalid = useMemo(() => {
    if (!text) return false;
    if (text.length < 10) return false; // still typing
    const iso = displayToIso(text);
    return !iso || !withinBounds(iso, min, max);
  }, [text, min, max]);

  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete={autoComplete}
        aria-label={ariaLabel}
        // Pattern is informational — we validate on parse to give a
        // friendlier UX than the native validation popover.
        pattern="\d{2}/\d{2}/\d{4}"
        placeholder={placeholder}
        maxLength={10}
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        onPaste={onTextPaste}
        required={required}
        data-testid={testId || "dob-text-input"}
        aria-invalid={hasInvalid ? "true" : "false"}
        className={`mt-1 w-full rounded-lg p-3 pr-11 border bg-white text-ink placeholder:text-[#9CA3AF] outline-none focus:ring-2 focus:ring-[#0E0E0E]/15 ${
          hasInvalid ? "border-[#C1272D]" : "border-[#E5E5E5]"
        }`}
      />
      <button
        type="button"
        onClick={openCalendar}
        aria-label="Pick date from calendar"
        title="Pick from calendar"
        className="absolute right-1 top-1/2 -translate-y-1/2 mt-[2px] inline-flex items-center justify-center h-9 w-9 rounded-md text-[#6B6B6B] hover:text-ink hover:bg-[#FAFAFA] transition-colors"
        data-testid={`${testId || "dob"}-open-calendar`}
      >
        <CalendarIcon size={18} weight="duotone" />
      </button>
      {/* Hidden native input — only used as the calendar surface. We
          do NOT make it the primary because on iOS that re-introduces
          the wheel-tap-tap-tap friction this whole component exists
          to solve. */}
      <input
        ref={calendarRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        min={min}
        max={max}
        value={value || ""}
        onChange={onCalendarChange}
        className="absolute right-2 top-3 h-0 w-0 opacity-0 pointer-events-none"
      />
    </div>
  );
}

/* ---------------- format helpers ---------------- */

/** ISO `YYYY-MM-DD` → display `MM/DD/YYYY`. Empty in, empty out. */
function isoToDisplay(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  return `${m[2]}/${m[3]}/${m[1]}`;
}

/** Display `MM/DD/YYYY` → ISO `YYYY-MM-DD`. Returns "" if invalid. */
function displayToIso(disp) {
  if (!disp) return "";
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(disp);
  if (!m) return "";
  const mm = m[1].padStart(2, "0");
  const dd = m[2].padStart(2, "0");
  const yyyy = m[3];
  const iso = `${yyyy}-${mm}-${dd}`;
  // Validate that the date actually exists (e.g. reject 02/30/2025).
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "";
  const back = parsed.toISOString().slice(0, 10);
  if (back !== iso) return "";  // 02/30 would coerce to 03/02 — reject.
  return iso;
}

/** Try to recover ISO from a non-MM/DD/YYYY paste shape. */
function tryIsoPaste(raw) {
  // `YYYY-MM-DD` shape
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (iso) return raw;
  // `M/D/YYYY` shape — already handled by displayToIso via the
  // 1-2 digit allowance. Nothing else to do.
  return "";
}

/**
 * Insert slashes as the user types. Strips non-digits, then formats:
 *   - 0-2 digits  → `M` or `MM`
 *   - 3-4 digits  → `MM/D` or `MM/DD`
 *   - 5-8 digits  → `MM/DD/YYYY`
 *
 * Critical: returns the user's text as-typed (no auto-zero-padding
 * mid-stream), only inserts slashes. That keeps the caret in the
 * expected position without us having to manage selection state.
 */
function formatAsTyped(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/** Inclusive ISO range check. Min/max are optional. */
function withinBounds(iso, min, max) {
  if (min && iso < min) return false;
  if (max && iso > max) return false;
  return true;
}
